import React from 'react';
import Modal from '../common/Modal';
import ModalContent from '../common/ModalContent';
import ModalCloseButton from '../common/ModalCloseButton';
import { Typography } from '../common/Typography';
import { getPlayerNameColor } from '../../utils/playerUtils';

/**
 * Shown when the host submits a score: who is queued to rotate onto that court next.
 * The host dismisses the modal when ready (not tied to save completion).
 *
 * @param {string[]|undefined} team1Ids
 * @param {string[]|undefined} team2Ids
 * @param {Object<string, object>|undefined} playersById — when set with ids, names use gender colours
 */
export default function ScoreRecordingNextUpModal({
  isOpen,
  onClose,
  activeCourtNumber,
  team1Names = [],
  team2Names = [],
  team1Ids = [],
  team2Ids = [],
  playersById = null,
  isComplete = false,
  hasAnyPlayers = false,
}) {
  if (!isOpen) return null;

  const courtLabel =
    activeCourtNumber != null && activeCourtNumber !== ''
      ? `Court ${activeCourtNumber}`
      : 'Next on court';

  const formatTeam = (names) =>
    Array.isArray(names) && names.length > 0 ? names.join(' & ') : '—';

  const renderTeamNamesColored = (ids, namesFallback) => {
    const idList = Array.isArray(ids) ? ids.filter(Boolean) : [];
    if (playersById && idList.length > 0) {
      const nodes = [];
      idList.forEach((id, idx) => {
        const p = playersById[id];
        const label = p?.name || namesFallback[idx] || '';
        if (!label) return;
        if (idx > 0) {
          nodes.push(
            <span key={`sep-${id}-${idx}`} className="mx-1 font-medium text-text-secondary">
              &
            </span>
          );
        }
        nodes.push(
          <span key={id} className={`font-semibold ${getPlayerNameColor(p?.gender)}`}>
            {label}
          </span>
        );
      });
      if (nodes.length > 0) {
        return <div className="text-base leading-snug">{nodes}</div>;
      }
    }
    return (
      <Typography variant="strong" as="div" className="!text-base text-text-primary">
        {formatTeam(namesFallback)}
      </Typography>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent className="max-w-md">
        <div className="flex justify-between items-start gap-3 mb-4">
          <div>
            <Typography variant="h4" as="h2" className="!mb-1">
              {courtLabel}
            </Typography>
            <Typography variant="body-small" className="!mb-0">
              Next players up — call them to the court when you are ready.
            </Typography>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        {!hasAnyPlayers ? (
          <div className="rounded-lg border border-border-medium bg-surface-subtle px-4 py-3">
            <Typography variant="body-small" className="!mb-0">
              No upcoming game is queued for this rotation yet. The session will fill this court
              after the new result is applied.
            </Typography>
          </div>
        ) : (
          <div className="space-y-3">
            {!isComplete && (
              <div className="rounded-lg border border-warning/40 bg-warning-light/80 px-3 py-2 dark:bg-warning/10">
                <Typography variant="caption" className="!text-warning-dark dark:!text-warning">
                  Lineup still filling — these are the players in the next queue slot.
                </Typography>
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-team1/30 bg-team1/5 px-4 py-3 dark:bg-team1/10">
                <Typography variant="label-small" className="!mb-1 !text-team1">
                  Team 1
                </Typography>
                {renderTeamNamesColored(team1Ids, team1Names)}
              </div>
              <div className="rounded-xl border border-team2/30 bg-team2/5 px-4 py-3 dark:bg-team2/10">
                <Typography variant="label-small" className="!mb-1 !text-team2">
                  Team 2
                </Typography>
                {renderTeamNamesColored(team2Ids, team2Names)}
              </div>
            </div>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
}
